<template>
    <div v-if="editMode" class="section-text-edit-anchor">
        <Teleport to="body">
            <!-- Fixed bottom edit bar (same style as canvas editor) -->
            <transition name="bottom-bar-slide">
                <div
                    v-if="barOpen && !subPanel"
                    class="bottom-edit-bar"
                    @click.stop
                >
                    <div class="bottom-edit-bar__inner">
                        <!-- Edit text -->
                        <button class="bbar-item" @click.stop="openSubPanel('text')">
                            <font-awesome-icon icon="fa-solid fa-i-cursor" />
                            <span class="bbar-label">Text</span>
                        </button>

                        <!-- Color -->
                        <div class="bbar-item bbar-item--color" @click.stop="openColorPicker">
                            <span class="bbar-color-chip" :style="{ backgroundColor: localColor }"></span>
                            <input
                                ref="colorInput"
                                type="color"
                                v-model="localColor"
                                class="bbar-color-input"
                                @input="saveColor"
                                @click.stop
                            />
                            <span class="bbar-label">Culoare</span>
                        </div>

                        <!-- Font -->
                        <button class="bbar-item" @click.stop="openSubPanel('font')">
                            <font-awesome-icon icon="fa-solid fa-font" />
                            <span class="bbar-label">Font</span>
                        </button>

                        <!-- Size (hidden in compact mode) -->
                        <button v-if="!compact" class="bbar-item" @click.stop="openSubPanel('size')">
                            <font-awesome-icon icon="fa-solid fa-text-height" />
                            <span class="bbar-label">Marime</span>
                        </button>

                        <!-- Shadow toggle (hidden in compact mode) -->
                        <button
                            v-if="!compact"
                            class="bbar-item"
                            :class="{ 'bbar-item--active': localShadow }"
                            @click.stop="toggleShadow"
                        >
                            <font-awesome-icon icon="fa-solid fa-clone" />
                            <span class="bbar-label">Umbra</span>
                        </button>

                        <!-- Margin (hidden in compact mode) -->
                        <button v-if="!compact" class="bbar-item" @click.stop="openSubPanel('margin')">
                            <font-awesome-icon icon="fa-solid fa-arrows-up-down" />
                            <span class="bbar-label">Spatiu</span>
                        </button>

                        <!-- Delete block (only for deletable text blocks) -->
                        <button
                            v-if="deletable"
                            class="bbar-item bbar-item--danger"
                            @click.stop="deleteBlock"
                        >
                            <font-awesome-icon icon="fa-solid fa-trash" />
                            <span class="bbar-label">Sterge</span>
                        </button>

                        <div class="bbar-divider"></div>

                        <!-- Close -->
                        <button class="bbar-item" @click.stop="closeAll">
                            <font-awesome-icon icon="fa-solid fa-xmark" />
                        </button>
                    </div>
                </div>
            </transition>

            <!-- Sub-panel overlay -->
            <transition name="action-sheet-fade">
                <div
                    v-if="subPanel"
                    class="action-sheet-overlay"
                    @click.stop="subPanel = null"
                ></div>
            </transition>

            <!-- Sub-panel: Edit text -->
            <transition name="action-sheet-slide">
                <div v-if="subPanel === 'text'" class="action-sheet" @click.stop>
                    <div class="action-sheet__header">
                        <button class="action-sheet__close" @click.stop="subPanel = null">
                            <font-awesome-icon icon="fa-solid fa-arrow-left" />
                        </button>
                        <span class="action-sheet__title">Modifica text</span>
                        <button class="action-sheet__close" @click.stop="closeAll">
                            <font-awesome-icon icon="fa-solid fa-xmark" />
                        </button>
                    </div>
                    <div class="section-edit-panel-body">
                        <textarea
                            ref="textInput"
                            v-model="localText"
                            class="section-edit-textarea"
                            rows="3"
                        ></textarea>
                        <button class="section-edit-save-btn" @click.stop="saveText">Actualizeaza</button>
                    </div>
                </div>
            </transition>

            <!-- Sub-panel: Size -->
            <transition name="action-sheet-slide">
                <div v-if="subPanel === 'size'" class="action-sheet" @click.stop>
                    <div class="action-sheet__header">
                        <button class="action-sheet__close" @click.stop="subPanel = null">
                            <font-awesome-icon icon="fa-solid fa-arrow-left" />
                        </button>
                        <span class="action-sheet__title">Dimensiune text</span>
                        <button class="action-sheet__close" @click.stop="closeAll">
                            <font-awesome-icon icon="fa-solid fa-xmark" />
                        </button>
                    </div>
                    <div class="section-edit-panel-body">
                        <div class="section-edit-size-grid">
                            <button
                                v-for="s in sizes"
                                :key="s.value"
                                class="section-edit-size-btn"
                                :class="{ 'section-edit-size-btn--active': localSize == s.value }"
                                @click.stop="localSize = s.value; saveSize()"
                            >
                                {{ s.label }}
                            </button>
                        </div>
                    </div>
                </div>
            </transition>

            <!-- Sub-panel: Font -->
            <transition name="action-sheet-slide">
                <div v-if="subPanel === 'font'" class="action-sheet" @click.stop>
                    <div class="action-sheet__header">
                        <button class="action-sheet__close" @click.stop="subPanel = null">
                            <font-awesome-icon icon="fa-solid fa-arrow-left" />
                        </button>
                        <span class="action-sheet__title">Font</span>
                        <button class="action-sheet__close" @click.stop="closeAll">
                            <font-awesome-icon icon="fa-solid fa-xmark" />
                        </button>
                    </div>
                    <div class="section-edit-panel-body" style="max-height: 50vh; overflow-y: auto;">
                        <button
                            v-for="f in fonts"
                            :key="f"
                            class="section-edit-font-item"
                            :class="{ 'section-edit-font-item--active': localFont === f }"
                            :style="{ fontFamily: f }"
                            @click.stop="localFont = f; saveFont()"
                        >
                            <span>{{ f }}</span>
                            <font-awesome-icon
                                v-if="localFont === f"
                                icon="fa-solid fa-check"
                                class="section-edit-font-check"
                            />
                        </button>
                    </div>
                </div>
            </transition>

            <!-- Sub-panel: Margin -->
            <transition name="action-sheet-slide">
                <div v-if="subPanel === 'margin'" class="action-sheet" @click.stop>
                    <div class="action-sheet__header">
                        <button class="action-sheet__close" @click.stop="subPanel = null">
                            <font-awesome-icon icon="fa-solid fa-arrow-left" />
                        </button>
                        <span class="action-sheet__title">Aliniere verticala</span>
                        <button class="action-sheet__close" @click.stop="closeAll">
                            <font-awesome-icon icon="fa-solid fa-xmark" />
                        </button>
                    </div>
                    <div class="section-edit-panel-body">
                        <input
                            type="range"
                            v-model="localMargin"
                            class="section-edit-range"
                            :min="marginConfig._min || 0"
                            :max="marginConfig._max || 50"
                            @input="saveMarginLive"
                        />
                        <span class="section-edit-range-value">{{ localMargin }}vh</span>
                    </div>
                </div>
            </transition>
        </Teleport>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import mitt from '@/utils/EventBus';

let _editorCounter = 0;

export default {
    props: {
        textValue: { type: String, default: '' },
        colorValue: { type: String, default: '#000000' },
        sizeValue: { default: 40 },
        shadowValue: { default: false },
        marginValue: { default: 0 },
        fontValue: { type: String, default: '' },
        fonts: { type: Array, default: () => [] },
        section: { type: Object, required: true },
        sectionKey: { type: String, required: true },
        textPropertyKey: { type: String, required: true },
        colorPropertyKey: { type: String, required: true },
        sizePropertyKey: { type: String, required: true },
        shadowPropertyKey: { type: String, required: true },
        marginPropertyKey: { type: String, required: true },
        fontPropertyKey: { type: String, required: true },
        marginConfig: { type: Object, default: () => ({ _min: 0, _max: 50 }) },
        compact: { type: Boolean, default: false },
        deletable: { type: Boolean, default: false },
    },
    data() {
        return {
            editorId: `steb-${++_editorCounter}`,
            barOpen: false,
            subPanel: null,
            localText: this.textValue,
            localColor: this.colorValue,
            localSize: this.sizeValue,
            localShadow: this.shadowValue,
            localMargin: this.marginValue,
            localFont: this.fontValue,
            sizes: [
                { label: 'XS', value: 10 },
                { label: 'S', value: 20 },
                { label: 'M', value: 40 },
                { label: 'L', value: 70 },
                { label: 'XL', value: 100 },
                { label: 'XXL', value: 140 },
            ],
        };
    },
    computed: {
        ...mapGetters(['isEditMode']),
        editMode() {
            return this.isEditMode;
        },
    },
    mounted() {
        mitt.on('section-editor-open', this.handleOtherOpen);
        document.addEventListener('click', this.onDocumentClick);
    },
    beforeUnmount() {
        mitt.off('section-editor-open', this.handleOtherOpen);
        document.removeEventListener('click', this.onDocumentClick);
    },
    methods: {
        handleOtherOpen(id) {
            if (id !== this.editorId) {
                this.barOpen = false;
                this.subPanel = null;
            }
        },
        open() {
            mitt.emit('section-editor-open', this.editorId);
            this.barOpen = true;
        },
        onDocumentClick() {
            if (this.barOpen && !this.subPanel) {
                this.barOpen = false;
            }
        },
        openSubPanel(panel) {
            this.subPanel = panel;
            if (panel === 'text') {
                this.$nextTick(() => {
                    const input = this.$refs.textInput;
                    if (input) input.focus();
                });
            }
        },
        openColorPicker() {
            this.$refs.colorInput.click();
        },
        closeAll() {
            this.barOpen = false;
            this.subPanel = null;
        },
        updateNestedValue(obj, path, value) {
            const keys = path.split('.');
            let o = obj;
            for (let i = 0; i < keys.length - 1; i++) {
                o = o[keys[i]];
            }
            o[keys[keys.length - 1]] = value;
        },
        emitUpdate() {
            this.$emit('update-section', this.sectionKey, this.section);
        },
        saveText() {
            this.updateNestedValue(this.section, this.textPropertyKey, this.localText);
            this.emitUpdate();
            this.subPanel = null;
        },
        saveColor() {
            this.updateNestedValue(this.section, this.colorPropertyKey, this.localColor);
            this.emitUpdate();
        },
        saveSize() {
            this.updateNestedValue(this.section, this.sizePropertyKey, this.localSize);
            this.emitUpdate();
            this.subPanel = null;
        },
        toggleShadow() {
            this.localShadow = !this.localShadow;
            this.updateNestedValue(this.section, this.shadowPropertyKey, this.localShadow);
            this.emitUpdate();
        },
        saveMarginLive() {
            this.updateNestedValue(this.section, this.marginPropertyKey, this.localMargin);
            this.emitUpdate();
        },
        saveFont() {
            this.updateNestedValue(this.section, this.fontPropertyKey, this.localFont);
            this.emitUpdate();
            this.subPanel = null;
        },
        deleteBlock() {
            this.closeAll();
            this.$emit('delete-block');
        },
    },
};
</script>

<style>
/* Anchor takes no space — everything is teleported */
.section-text-edit-anchor {
    position: absolute;
    width: 0;
    height: 0;
    overflow: hidden;
    pointer-events: none;
}

/* ── Section text edit bar specific styles ── */
.section-edit-panel-body {
    padding: 12px 20px max(16px, env(safe-area-inset-bottom));
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.section-edit-textarea {
    width: 100%;
    min-height: 80px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 16px;
    font-family: inherit;
    line-height: 1.4;
    outline: none;
    resize: vertical;
}

.section-edit-textarea:focus {
    border-color: #16a34a;
    box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.15);
}

.section-edit-save-btn {
    width: 100%;
    height: 44px;
    border: none;
    border-radius: 10px;
    background: #16a34a;
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease;
}

.section-edit-save-btn:hover {
    background: #15803d;
}

.section-edit-size-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}

.section-edit-size-btn {
    height: 44px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #f8fafc;
    color: #334155;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.12s ease, border-color 0.12s ease;
}

.section-edit-size-btn:hover {
    background: #f0fdf4;
    border-color: #86efac;
}

.section-edit-size-btn--active {
    background: #16a34a;
    color: #fff;
    border-color: #16a34a;
}

.section-edit-font-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 12px 0;
    border: none;
    background: transparent;
    font-size: 18px;
    color: #334155;
    cursor: pointer;
    border-bottom: 1px solid #f1f5f9;
    text-align: left;
}

.section-edit-font-item:last-child {
    border-bottom: none;
}

.section-edit-font-item--active {
    color: #16a34a;
    font-weight: 600;
}

.section-edit-font-check {
    color: #16a34a;
    font-size: 16px;
}

.section-edit-range {
    width: 100%;
    accent-color: #16a34a;
}

.section-edit-range-value {
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    color: #64748b;
}

.bbar-item--danger .bbar-label {
    color: #dc2626;
}
</style>
